import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useDeleteSelectionShortcut } from './useDeleteSelectionShortcut';
import { useEmailListActions } from './useEmailListActions';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { useCustomToast } from '../useCustomToast';

interface UseInboxSpamActionsProps {
  filteredCommunications: UserCommunication[];
  selectedItems: Set<string>;
  toggleItemSelection: (messageId: string) => void;
  clearSelection?: () => void;
}

export function useInboxSpamActions({
  filteredCommunications,
  selectedItems,
  toggleItemSelection,
  clearSelection,
}: UseInboxSpamActionsProps) {
  useEmailListActions({
    filteredCommunications,
    context: ShortcutContext.SPAM,
    selectedItems,
    toggleItemSelection,
    clearSelection,
  });

  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const { selectedEmailIndex } = useShortcutsContext();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();
  const deleteHandler = useDeleteSelectionShortcut({
    filteredCommunications,
    selectedItems,
    selectedEmailIndex,
    clearSelection,
  });

  useEffect(() => {
    const ctx = [ShortcutContext.SPAM];
    unregisterShortcut(ShortcutKey.DELETE, ctx);
    unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
    unregisterShortcut(ShortcutKey.BACKSPACE, ctx);

    registerShortcut({
      key: ShortcutKey.DELETE,
      contexts: [ShortcutContext.SPAM],
      handler: deleteHandler,
      description: 'Delete selected spam email(s)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE_KEY,
      contexts: [ShortcutContext.SPAM],
      handler: deleteHandler,
      description: 'Delete selected spam email(s)',
    });

    registerShortcut({
      key: ShortcutKey.BACKSPACE,
      contexts: [ShortcutContext.SPAM],
      handler: deleteHandler,
      description: 'Delete selected spam email(s)',
    });

    return () => {
      unregisterShortcut(ShortcutKey.DELETE, ctx);
      unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
      unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    selectedItems,
    clearSelection,
    deleteHandler,
    t,
    promiseToast,
  ]);
}
